/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiResponse, Configuration, mockSuccessResponse } from '@core/models';
import { Client } from '@truckstop-tms/zuum-lib';
import { MongoUser, USER_TYPES } from '@core/models/mongo';
import { Observable, of, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { ApiService } from '../../services/api.service';
import { LocalStorageService, LOCAL_STORAGE_TOKEN } from './library/local-storage.service';
import { BaseStorageKeys, BaseStorageService, PREFIXES } from './base-storage.service';
import { environment } from 'environments/environment';
import { MOCK_AUTH_PAYLOAD, MOCK_CORE_TOKEN_PAYLOAD } from '@core/mocks/mock-services';

const USE_MOCK_SERVICES = (environment as { useMockServices?: boolean }).useMockServices ?? false;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private apiService: ApiService,
    private http: HttpClient,
    @Inject(LOCAL_STORAGE_TOKEN) private localStorageService: LocalStorageService,
    private baseStorageService: BaseStorageService
  ) {}

  login(user: MongoUser): Observable<ApiResponse> {
    if (USE_MOCK_SERVICES) {
      const mockedUser = {
        ...MOCK_AUTH_PAYLOAD,
        user: {
          ...MOCK_AUTH_PAYLOAD.user,
          email: user.email,
        },
      };
      return of(mockSuccessResponse(mockedUser));
    }

    const headers = new HttpHeaders()
      .set('email', user.email)
      .set('password', user.password || '')
      .set('type', USER_TYPES.SUPER_ADMIN_MANAGER)
      .set('tenantId', 'ZUUM');

    return this.http
      .get<ApiResponse>(this.apiService.login, { headers })
      .pipe(catchError((error: any) => throwError(error)));
  }

  fetchCoreToken(email: string, password: string, type = USER_TYPES.SUPER_ADMIN_MANAGER): Observable<ApiResponse> {
    if (USE_MOCK_SERVICES) {
      return of(mockSuccessResponse(MOCK_CORE_TOKEN_PAYLOAD));
    }
    return this.http
      .post<ApiResponse>(`${this.apiService.superAdminApi}/auth/core-api`, { email, password, type })
      .pipe(catchError((error: any) => throwError(error)));
  }

  // Set info on Local storage
  setCredentials(data: any): void {
    this.baseStorageService.set(BaseStorageKeys.AuthUser, data.user);
    this.baseStorageService.set(BaseStorageKeys.AuthToken, data.token);
    this.baseStorageService.set(BaseStorageKeys.Permissions, data.permissionObj);
  }

  setClient(data: Client): void {
    this.baseStorageService.set(BaseStorageKeys.Client, { id: data.id });
    this.setClientConfiguration(data.settingsConfiguration);
  }

  setClientConfiguration(data: Configuration[]): void {
    this.localStorageService.setClientSettings(data);
  }

  setCoreToken(data: string): void {
    this.baseStorageService.set(BaseStorageKeys.CoreToken, data);
  }

  setSecurityToken(token: string): void {
    this.baseStorageService.set(BaseStorageKeys.SuperAdminApiToken, token, PREFIXES.ZM);
  }

  logout(): void {
    localStorage.clear();
  }

  get authToken(): string {
    return this.baseStorageService.get(BaseStorageKeys.AuthToken) || '';
  }

  get coreToken(): string {
    return this.baseStorageService.get(BaseStorageKeys.CoreToken) || '';
  }

  get authUser(): MongoUser {
    return this.baseStorageService.get<any>(BaseStorageKeys.AuthUser);
  }

  get client(): MongoUser {
    return this.baseStorageService.get<any>(BaseStorageKeys.Client);
  }

  get isLoggedIn(): string | null {
    return this.baseStorageService.get(BaseStorageKeys.AuthToken);
  }

  get securityToken(): string {
    return this.baseStorageService.get(BaseStorageKeys.SuperAdminApiToken, PREFIXES.ZM) || '';
  }

  get clientFeatures(): Configuration[] {
    return this.baseStorageService.get<Configuration[]>(BaseStorageKeys.ClientConfig) || [];
  }

  getSecureApiKey(): void {
    if (environment.apiKeyPAASEnabled) {
      const subdomain = document.location.host === 'localhost:4600' ? 'dev-admin.zuumapp.com' : document.location.host;
      const url = `${this.apiService.superAdminApi}/clients/subdomain/${subdomain}?key=${environment.superAdminApiKey}`;
      this.http
        .get(url)
        .pipe(take(1))
        .subscribe((res: any) => {
          if (res.result.success && res.data) {
            this.setSecurityToken(res.data.apiKey);
          }
        });
    }
  }
}
